import React from "react";
import { useFormik } from "formik";
import { useTranslations } from "next-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { Select, ShowWhen } from "@/components/common";
import SelectPreDeliveryAddressFlow from "@/components/common/select/address-select/select-predelivery-address-flow";
import { CloseModalIcon, EditIcon, UserIcon } from "@/components/assets/icons";
import { apiRequest } from "@/utils/apiRequest";
import {
  EDIT_PREDELIVERY_ADDRESS,
  GET_SHIPMENT_LIST,
  VIEW_PARCEL,
} from "@/constants/Query";

type TProps = {
  startEditing: () => void;
  closeEditing: () => void;
  isEditing: boolean;
  isPreDeliveryAddressEditable: boolean;
  addressId: number;
  addressText: string;
  shipmentId: number;
};

const PredeliveryAddress = ({
  startEditing,
  closeEditing,
  isEditing,
  isPreDeliveryAddressEditable,
  addressId = 641183,
  addressText,
  shipmentId,
}: TProps) => {
  const queryClient = useQueryClient();

  const t = useTranslations();

  const { values, setFieldValue, resetForm, handleSubmit } = useFormik({
    initialValues: {
      predeliveryAddressId: { id: addressId, mainText: addressText },
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        await changePreDeliveryAddress({
          shipmentId,
          addressId: values.predeliveryAddressId?.id,
          addressTypeId: 1,
        });
        closeEditing();
        queryClient.invalidateQueries({ queryKey: [GET_SHIPMENT_LIST] });
        queryClient.invalidateQueries({ queryKey: [VIEW_PARCEL] });
        toast.success("Success", {
          position: "top-center",
        });
      } catch (error) {
        closeEditing();
        toast.error("Error occured", {
          position: "top-center",
        });
      }
    },
  });

  const { mutateAsync: changePreDeliveryAddress } = useMutation({
    mutationFn: (data: {
      shipmentId: number;
      addressTypeId: number;
      addressId: number;
    }) => apiRequest(EDIT_PREDELIVERY_ADDRESS, "put", data),
  });

  return (
    <>
      <ShowWhen condition={!isEditing}>
        <div className="flex items-center gap-2">
          <p className="text-black">{addressText || ""}</p>
          <ShowWhen condition={isPreDeliveryAddressEditable}>
            <span className="primary-bg cursor-pointer rounded-full p-1">
              <EditIcon onClick={startEditing} className="fill-white" />
            </span>
          </ShowWhen>
        </div>
      </ShowWhen>
      <ShowWhen condition={isEditing}>
        <div className="flex w-full items-center justify-end gap-4">
          <Select
            label={"delivery_address"}
            containerClassName="!max-w-[300px]"
            translation={false}
            selectedValue={values.predeliveryAddressId}
            name="predeliveryAddressId"
            mainIcon={<UserIcon />}
          >
            <SelectPreDeliveryAddressFlow
              selectedOption={values.predeliveryAddressId}
              onSelect={(selectedValue) => {
                setFieldValue("predeliveryAddressId", selectedValue);
                handleSubmit();
              }}
            />
          </Select>
          <span className="primary-bg p-1f h-8 w-8 cursor-pointer rounded-full">
            <CloseModalIcon
              onClick={() => {
                closeEditing();
                resetForm();
              }}
              className="fill-white"
            />
          </span>
        </div>
      </ShowWhen>
    </>
  );
};

export default PredeliveryAddress;
